import { useCallback, useEffect, useState } from 'react'
import { api, isNotFound, methodFile, unavailableOf, type Unavailable } from '../v4/api'
import type { Conversation, Period, Plan, Progress, RunState } from '../v4/contracts'
import { followRun, type Followed } from '../v4/follow'
import { isStillLoading } from './unavailableText'

/**
 * The old builder's controller over one v4 run: the run as the API shows it, its progress, and the
 * actions the engine offers (start, approve, answer an ask, send a method file, cancel, rerun).
 * Following a run is `followRun`, the same loop the v4 page uses, so both surfaces see the same run
 * the same way. Errors are the API's: a 409/503 refusal becomes `unavailable` (why workflows cannot
 * run for this organization right now), anything else the `error` line.
 */
export interface WorkflowRun {
  runId: string | null
  run: RunState | null
  progress: Progress | null
  /** When `progress` was received (epoch ms), so a running clock can count on from it. */
  progressAt: number
  /** A followed run whose first state has not landed yet. */
  loading: boolean
  /** An action is in flight. */
  busy: boolean
  error: string
  /**
   * A request outlived the browser's deadline; the server goes on with it. `running`: the request
   * was about a run this page follows (planning after an answer or a file takes minutes), so the
   * run's own updates clear it. `not_started`: a start or rerun that never handed back a run id, so
   * there is nothing to follow and the engineer has to look again or ask again.
   */
  slow: 'running' | 'not_started' | null
  unavailable: Unavailable | null
  /** The API has no such run for this organization (404): gone, or another organization's. */
  notFound: boolean
  /** Starts a run from a question, as a follow-up when a conversation is given; the new run's id, or null when the API refused. */
  start(question: string, conversation?: Conversation): Promise<string | null>
  /** Approves the run's current plan version (the first node's Run pill). */
  approve(): Promise<void>
  cancel(): Promise<void>
  answer(resumeToken: string, values: Record<string, string>): Promise<void>
  sendFile(file: File): Promise<void>
  /** The approved plan on a new period, as a new run; its id, or null when refused. */
  rerun(period: Period): Promise<string | null>
  /** The plan awaiting approval, edited: true once the engine took it as a new version; false when it refused (the reason is the error line). */
  editPlan(plan: Plan): Promise<boolean>
  clearError(): void
}

export function useWorkflowRun(runId: string | null): WorkflowRun {
  const [run, setRun] = useState<RunState | null>(null)
  const [followed, setFollowed] = useState<Followed | null>(null)
  const [error, setError] = useState('')
  const [slow, setSlow] = useState<WorkflowRun['slow']>(null)
  const [unavailable, setUnavailable] = useState<Unavailable | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [busy, setBusy] = useState(false)

  const fail = useCallback((e: Error, startsRun = false) => {
    const u = unavailableOf(e)
    if (isNotFound(e)) setNotFound(true)
    else if ((e as Error & { isTimeout?: boolean }).isTimeout) setSlow(startsRun ? 'not_started' : 'running')
    else if (u) setUnavailable(u)
    else setError(e.message)
  }, [])

  // followRun keeps a record of runs by id (the v4 page follows a group); this hook follows one.
  useEffect(() => {
    setRun(null)
    setFollowed(null)
    setError('')
    setSlow(null)
    setUnavailable(null)
    setNotFound(false)
    if (!runId) return
    // The run answered again (its state, or the progress polled every 2 s): a "still loading" or
    // "taking longer than usual" notice is over, so it does not stay on a run that is moving.
    const answered = () => {
      setSlow(null)
      setUnavailable((u) => (u && isStillLoading(u) ? null : u))
    }
    return followRun(
      runId,
      (f) => {
        answered()
        setRun((prev) => f(prev ? { [runId]: prev } : {})[runId] ?? null)
      },
      (f) => {
        answered()
        setFollowed((prev) => f(prev ? { [runId]: prev } : {})[runId] ?? null)
      },
      fail,
      // This page reads only what each call did (runStory.stepFailure); the whole calls, every
      // prompt and tool row, made an admin's load take seconds (FS-724).
      'summary',
    )
  }, [runId, fail])

  const act = useCallback(
    async <T,>(fn: () => Promise<T>, startsRun = false): Promise<T | null> => {
      setError('')
      setSlow(null)
      setUnavailable(null)
      setBusy(true)
      try {
        return await fn()
      } catch (e) {
        fail(e as Error, startsRun)
        return null
      } finally {
        setBusy(false)
      }
    },
    [fail],
  )

  const start = useCallback(
    (question: string, conversation?: Conversation) =>
      act(() => (conversation ? api.start(question, conversation) : api.start(question)), true).then((r) => r?.runIds[0] ?? null),
    [act],
  )
  const approve = useCallback(async () => {
    if (!run) return
    await act(() => api.approve(run.runId, run.planVersion))
  }, [act, run])
  const cancel = useCallback(async () => {
    if (!runId) return
    await act(() => api.cancel(runId))
  }, [act, runId])
  const answer = useCallback(
    async (resumeToken: string, values: Record<string, string>) => {
      await act(() => api.answer(resumeToken, values))
    },
    [act],
  )
  const sendFile = useCallback(
    async (file: File) => {
      if (!runId) return
      await act(async () => api.sendFile(runId, await methodFile(file)))
    },
    [act, runId],
  )
  const rerun = useCallback(
    (period: Period) =>
      runId ? act(() => api.rerun(runId, period), true).then((r) => r?.runIds[0] ?? null) : Promise.resolve(null),
    [act, runId],
  )
  const editPlan = useCallback(
    async (plan: Plan) => (runId ? (await act(() => api.edit(runId, plan))) !== null : false),
    [act, runId],
  )
  const clearError = useCallback(() => {
    setError('')
    setSlow(null)
    setUnavailable(null)
  }, [])

  return {
    runId,
    run,
    progress: followed?.p ?? null,
    progressAt: followed?.at ?? 0,
    loading: runId !== null && run === null && !error && !unavailable && !notFound,
    busy,
    error,
    slow,
    unavailable,
    notFound,
    start,
    approve,
    cancel,
    answer,
    sendFile,
    rerun,
    editPlan,
    clearError,
  }
}
